<template>
  <v-row>
    <v-col>
      <v-card>
        <v-toolbar :dark="$vuetify.theme.dark" color="primary">
          <v-toolbar-title>
            {{ $t('Мастер создания запросов на регистрацию Уполномоченного представителя') }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col>
              <request-result-info
                  :process-comment="request.processComment"
                  :request-status="request.status" />
            </v-col>
          </v-row>
          <custom-stepper
              v-model="stepNumber"
              :steps-param-array="stepsProperties"
              :loading="isLoading"
              :disabled-forward-btn="isLoading"
              :success="success">
            <template v-slot:main-1>
              <user-profile
                  ref="userProfileInfo"
                  :profile-info="profile"
                  :readonly="readonly" />
            </template>
            <template v-slot:main-2>
              <v-row>
                <v-col>
                  <identity-document-list
                      ref="requestFiles"
                      :request-files="requestFiles"
                      :document-types="requiredDocumentTypes"
                      :readonly="readonly"
                      :loading="isLoading"
                      @uploadFile="uploadFile" />
                </v-col>
              </v-row>
            </template>
            <template v-slot:main-3>
              <v-row>
                <v-col>
                  <user-registration-request-view
                      :profile-info="profile">
                    <template v-slot:additional>
                      <v-expansion-panel v-test="{ panel: '' }">
                        <v-expansion-panel-header>
                          <div id="attach_file">
                            <v-icon>
                              attach_file
                            </v-icon>
                            {{ $t('Прикрепленные документы') }}
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content eager>
                          <identity-document-list-view
                              :request-files="requestFiles"
                              :document-types="requiredDocumentTypes"
                              :loading="isLoading" />
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </template>
                  </user-registration-request-view>
                </v-col>
              </v-row>
            </template>
            <template v-slot:buttons-3>
              <v-btn
                  v-if="canSendRequest"
                  id="sendBtn"
                  color="primary"
                  :disabled="isLoading"
                  @click="sendUserRegistrationRequest">
                {{ $t('Отправить') }}
              </v-btn>
            </template>
          </custom-stepper>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
  import Vue from 'vue'
  import CustomStepper from '@/components/reused/ui/CustomStepper.vue'
  import UserProfile from './UserProfile.vue'
  import UserRegistrationRequestView from '@/components/reused/user/UserRegistrationRequestView.vue'
  import IdentityDocumentList from '@/components/reused/files/IdentityDocumentList.vue'
  import IdentityDocumentListView from '@/components/reused/files/IdentityDocumentListView.vue'
  import RequestResultInfo from './RequestResultInfo.vue'
  import { REQUEST_STATUSES, DOCUMENT_TYPES, IDENTITY_REQUEST_TYPES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { USER_PROFILE, IDENTITY_FILES, CURRENT_AUTHORIZATION_DOCUMENTS } from '@/constants/store.js'

  export default {
    components: {
      CustomStepper,
      UserProfile,
      IdentityDocumentList,
      IdentityDocumentListView,
      UserRegistrationRequestView,
      RequestResultInfo
    },
    data () {
      return {
        stepNumber: 1,
        isLoading: false,
        success: false
      }
    },
    computed: {
      ...mapState(USER_PROFILE.NAMESPACE, {
        profile: state => (state[USER_PROFILE.STATE.USER_PROFILE] || { }),
        request: state => (state[USER_PROFILE.STATE.REQUEST] || { })
      }),
      ...mapState(IDENTITY_FILES.NAMESPACE, {
        files: state => (state[IDENTITY_FILES.STATE.REQUEST_FILES] || []),
        requiredDocumentTypes: state => (state[IDENTITY_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      stepsProperties () {
        return [
          {
            stepNumber: 1,
            stepTitle: this.$t('Добавление информации о пользователе'),
            stepTip: this.$t('На данном этапе текущего пользователя можно связать с существующей в системе организацией'),
            successText: this.$t('Изменения сохранены'),
            toNextStep: this.validateAndSaveUserProfile,
            toPrevStep: null,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 2,
            stepTitle: this.$t('Добавление документов'),
            stepTip: '',
            successText: '',
            toNextStep: this.validateAndSyncRequestFiles,
            toPrevStep: this.syncRequestFiles,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 3,
            stepTitle: this.$t('Отправка запроса на регистрацию'),
            stepTip: '',
            successText: this.$t('Запрос успешно отправлен на обработку.'),
            toNextStep: null,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          }
        ]
      },
      readonly () {
        return !!this.profile.inn && !this.canSendRequest
      },
      canSendRequest () {
        const status = this.request.status
        if (status === REQUEST_STATUSES.INPROCESS ||
          status === REQUEST_STATUSES.APPROVED) {
          return false
        }
        return true
      },
      requestType () {
        return IDENTITY_REQUEST_TYPES.ORGANIZATION_AGENT
      },
      requestFiles () {
        const vm = this
        // const requestFiles = vm.files
        // const authorizationDocuments = [DOCUMENT_TYPES.USER_AUTHORIZATION_DOCUMENT]
        return vm.files
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.initialize()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(USER_PROFILE.NAMESPACE, {
        getUserProfileAction: USER_PROFILE.ACTIONS.GET_USER_PROFILE,
        updateUserProfileAction: USER_PROFILE.ACTIONS.UPDATE_USER_PROFILE,
        getUserRegistrationRequestAction: USER_PROFILE.ACTIONS.GET_USER_REGISTRATION_REQUEST,
        sendUserRegistrationRequestAction: USER_PROFILE.ACTIONS.SEND_USER_REGISTRATION_REQUEST,
        clearUserProfileAction: USER_PROFILE.ACTIONS.CLEAR_USER_PROFILE,
        clearUserRegistrationRequestAction: USER_PROFILE.ACTIONS.CLEAR_USER_REGISTRATION_REQUEST
      }),
      ...mapActions(IDENTITY_FILES.NAMESPACE, {
        getRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.GET_REQUIRED_DOCUMENT_TYPES,
        getRequestFilesAction: IDENTITY_FILES.ACTIONS.GET_REQUEST_FILES,
        linkRequestFileAction: IDENTITY_FILES.ACTIONS.LINK_REQUEST_FILE,
        clearRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.CLEAR_REQUIRED_DOCUMENT_TYPES,
        clearRequestFilesAction: IDENTITY_FILES.ACTIONS.CLEAR_REQUEST_FILES
      }),
      ...mapActions(CURRENT_AUTHORIZATION_DOCUMENTS.NAMESPACE, {
        createUserAuthorizationDocumentAction: CURRENT_AUTHORIZATION_DOCUMENTS.ACTIONS.CREATE_USER_AUTHORIZATION_DOCUMENT
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearUserProfileAction(),
            this.clearUserRegistrationRequestAction(),
            this.clearRequiredDocumentTypesAction(),
            this.clearRequestFilesAction()
          ])
      },
      initialize: async function () {
        this.isLoading = true
        try {
          await this.getUserProfileAction()
          if (this.profile.inn) {
            await this.getUserRegistrationRequestAction()
          }
          // если запрос одобрен, то требуется перезайти в систему
          if (!this.isRegistered &&
            this.request.status === REQUEST_STATUSES.APPROVED) {
            await this.getCurrentUserInfo()
          }
          await this.getRequestFiles()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getRequestFiles: async function () {
        const model = { requestType: this.requestType }

        if (this.request.requestId) {
          model.objectId = this.request.requestId
          await Promise.all([
            this.getRequiredDocumentTypesAction(model),
            this.getRequestFilesAction(model)
          ])
        } else {
          await this.getRequiredDocumentTypesAction(model)
        }
      },
      validateAndSaveUserProfile: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }

          const result = await this.$refs.userProfileInfo.validate()
          if (!result) {
            throw new Vue.errorsTypes.ValidationError()
          }
          await this.updateUserProfileAction(this.profile)

          await this.getRequestFiles()

          this.toNextStep(step)
        } catch (error) {
          this.throwIfUnknownError(error)
          // если ошибка, дальше не переходим
        }
      },
      validateAndSyncRequestFiles: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }
          // проверяем данные документов
          await this.$refs.requestFiles.validate()
          // в случае успешной проверки синхронизируем с хранилищем $store
          await this.$refs.requestFiles.syncronizeWithStoreData()
          //  на следующий шаг
          this.toNextStep(step)
        } catch (error) {
          this.throwIfNotValidationError(error)
          // если ошибка, дальше не переходим
        }
      },
      syncRequestFiles: async function (step) {
        try {
          if (this.readonly) {
            this.toPrevStep(step)
            return
          }
          // синхронизируем с хранилищем $store,
          // если что-то поменяли и хотим вернуться на предыдущий шаг
          await this.$refs.requestFiles.syncronizeWithStoreData()

          //  на предыдущий шаг
          this.toPrevStep(step)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error)
          // если ошибка, дальше не переходим
        }
      },
      sendUserRegistrationRequest: async function () {
        this.success = false
        try {
          const requestTypes = [
            IDENTITY_REQUEST_TYPES.ORGANIZATION_AGENT]
          if (requestTypes.includes(this.requestType)) {
            await this.createUserAuthorizationDocumentThrowIfError(this.requestFiles)
          }
          this.isLoading = true
          await this.sendUserRegistrationRequestAction(this.requestFiles)
          await this.getRequestFiles()
          this.isLoading = false
          this.success = true
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
          // для запрета перехода на следующий шаг пробрасываем ошибку
        }
      },
      createUserAuthorizationDocumentThrowIfError: async function (signedFiles) {
        const authorizationDocuments = [DOCUMENT_TYPES.USER_AUTHORIZATION_DOCUMENT]
        const authorizationDocumentFile = signedFiles.find(af => authorizationDocuments.includes(af.documentType))
        if (!authorizationDocumentFile) {
          throw new Vue.errorsTypes.ValidationError(this.$t('Документ, подтверждающий полномочия представителя не найден.'))
        }
        // if (authorizationDocumentFile.data?.contactPerson !== this.profile.contactPerson) {
        //   throw new Vue.errorsTypes.ValidationError(this.$t('Контактное лицо не совпадает с данными из профиля.'))
        // }
        if (!authorizationDocumentFile.data?.dateEnd ||
          !authorizationDocumentFile.data?.dateBegin) {
          throw new Vue.errorsTypes.ValidationError(this.$t('Обязательные поля Документа, подтверждающего полномочия представителя Пользователя ЭПЭ, не заполнены.'))
        }
        await this.createUserAuthorizationDocument(authorizationDocumentFile)
      },
      createUserAuthorizationDocument: async function (authorizationDocumentFile) {
        this.isLoading = true
        const authorizationDocument = {
          ...authorizationDocumentFile.data,
          file: {
            fileId: authorizationDocumentFile.fileId
          }
        }
        await this.createUserAuthorizationDocumentAction({
          authorizationDocument
        })
        this.isLoading = false
      },
      toNextStep: async function (step) {
        this.success = false
        this.stepNumber = step + 1
      },
      toPrevStep: function (step) {
        this.success = false
        this.stepNumber = step - 1
      },
      exitFromWizard: function () {
        this.goToRoute(`${this.urlRootPath}/user/registration/start`)
      },
      uploadFile: async function (fileId) {
        if (!this.request?.requestId) {
          await this.getUserRegistrationRequestAction()
        }

        const params = {
          requestType: this.requestType,
          objectId: this.request.requestId,
          model: { fileId }
        }
        await this.linkRequestFileAction(params)
        await this.getRequestFiles()
      }
    }
  }
</script>
